import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, 
  ChevronRight, 
  Loader2,
  Book,
  Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScriptureExplorer = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialVolume = searchParams.get('volume') || 'biblia';

  const [scriptures, setScriptures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVolume, setSelectedVolume] = useState(initialVolume);
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchScriptures = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/scriptures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScriptures(data);
      } else {
        toast.error('Error al cargar las escrituras');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchScriptures();
  }, [fetchScriptures]);

  const handleSelectChapter = (volume, book, chapter) => {
    navigate(`/study/${volume}/${book}/${chapter}`);
  };

  const filteredBooks = scriptures?.[selectedVolume]?.books.filter(book =>
    book.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  const volumeKeys = Object.keys(scriptures || {});
  const volumeLabels = {
    biblia: 'Biblia',
    libro_de_mormon: 'Libro de Mormón',
    doctrina_y_convenios: 'D&C',
    perla_de_gran_precio: 'PGP'
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="scripture-explorer">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-primary" />
            Explorador de Escrituras
          </h1>
          <p className="text-muted-foreground">
            Selecciona un libro y capítulo para estudiar
          </p>
        </div>

        {/* Volume Tabs */}
        <Tabs value={selectedVolume} onValueChange={setSelectedVolume}>
          <TabsList className="grid w-full grid-cols-4 h-auto">
            {volumeKeys.map((key) => (
              <TabsTrigger 
                key={key} 
                value={key} 
                className="text-xs sm:text-sm py-2"
                data-testid={`tab-${key}`}
              >
                {volumeLabels[key]}
              </TabsTrigger>
            ))}
          </TabsList>

          {volumeKeys.map((volumeKey) => (
            <TabsContent key={volumeKey} value={volumeKey} className="mt-6">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Books List */}
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Book className="h-5 w-5 text-primary" />
                      Libros
                    </CardTitle>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar libro..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-books"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-1">
                        {filteredBooks.map((book) => (
                          <Button
                            key={book.id}
                            variant={selectedBook?.id === book.id ? "secondary" : "ghost"}
                            className="w-full justify-between text-left h-auto py-2"
                            onClick={() => setSelectedBook(book)}
                            data-testid={`book-${book.id}`}
                          >
                            <span className="truncate">{book.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {book.chapters} cap.
                            </span>
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Chapters Grid */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading">
                      {selectedBook ? (
                        <span className="flex items-center gap-2">
                          <ChevronRight className="h-5 w-5 text-primary" />
                          {selectedBook.name}
                        </span>
                      ) : (
                        'Selecciona un libro'
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedBook ? (
                      <ScrollArea className="h-[400px]">
                        <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                          {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((chapter) => (
                            <Button
                              key={chapter}
                              variant="outline"
                              size="sm"
                              className="h-10 w-10 p-0 hover:bg-primary hover:text-primary-foreground transition-colors"
                              onClick={() => handleSelectChapter(volumeKey, selectedBook.id, chapter)}
                              data-testid={`chapter-${chapter}`}
                            >
                              {chapter}
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                        <BookOpen className="h-16 w-16 mb-4 opacity-20" />
                        <p>Selecciona un libro para ver los capítulos</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ScriptureExplorer;
